import { useEffect, useRef, useState, type ReactNode, type Ref } from 'react'
import { createPortal } from 'react-dom'
import { CheckIcon } from './icons'
import './sheet.css'

/**
 * A bottom sheet that sits on top of the keyboard.
 *
 * Its bottom edge is `--keyboard` (see `useKeyboardInset`), so when iOS
 * raises the keyboard the sheet rides up with it instead of staying under
 * it. Dragging the handle down, tapping the dim area or Escape closes it.
 *
 * `keepMounted` leaves it in the DOM, inert, while closed. The quick add sheet
 * needs that: iOS only raises the keyboard for a `focus()` made inside the
 * tap itself, so the field has to exist before the tap, not after it.
 *
 * It is drawn at the end of the page's body rather than where it is used. On
 * a laptop the idea panel sits inside the fixed canvas layout, which is a
 * stacking context of its own, and a sheet opened from there would otherwise
 * slide in under the rail.
 */
export function Sheet({
  open,
  onClose,
  label,
  children,
  keepMounted = false,
  layerRef,
  className,
}: {
  open: boolean
  onClose: () => void
  label: string
  children: ReactNode
  keepMounted?: boolean
  layerRef?: Ref<HTMLDivElement>
  className?: string
}) {
  const [mounted, setMounted] = useState(open || keepMounted)
  const [shown, setShown] = useState(false)
  const panel = useRef<HTMLDivElement>(null)
  const drag = useRef<{ startY: number; dy: number } | null>(null)

  useEffect(() => {
    if (open) {
      setMounted(true)
      // One frame mounted-but-hidden, so the slide has a start to animate from.
      const frame = requestAnimationFrame(() => setShown(true))
      return () => cancelAnimationFrame(frame)
    }
    setShown(false)
    if (keepMounted) return
    const timer = setTimeout(() => setMounted(false), 260)
    return () => clearTimeout(timer)
  }, [open, keepMounted])

  useEffect(() => {
    if (!open) return
    const root = document.documentElement
    root.classList.add('scroll-locked')
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      root.classList.remove('scroll-locked')
      window.removeEventListener('keydown', onKey)
    }
  }, [open, onClose])

  if (!mounted) return null

  const setOffset = (dy: number) => {
    panel.current?.style.setProperty('--drag', `${dy}px`)
  }

  return createPortal(
    <div
      ref={layerRef}
      className={`sheet-layer ${className ?? ''}`}
      data-open={open && shown}
      inert={!open}
    >
      <div className="sheet-backdrop" onClick={onClose} />
      <div ref={panel} className="sheet" role="dialog" aria-modal="true" aria-label={label}>
        <div
          className="sheet-grip"
          onPointerDown={(event) => {
            drag.current = { startY: event.clientY, dy: 0 }
            event.currentTarget.setPointerCapture(event.pointerId)
            panel.current?.classList.add('dragging')
          }}
          onPointerMove={(event) => {
            if (!drag.current) return
            drag.current.dy = Math.max(0, event.clientY - drag.current.startY)
            setOffset(drag.current.dy)
          }}
          onPointerUp={() => {
            const dy = drag.current?.dy ?? 0
            drag.current = null
            panel.current?.classList.remove('dragging')
            setOffset(0)
            if (dy > 70) onClose()
          }}
          onPointerCancel={() => {
            drag.current = null
            panel.current?.classList.remove('dragging')
            setOffset(0)
          }}
        >
          <span />
        </div>
        {children}
      </div>
    </div>,
    document.body,
  )
}

export interface Option<T extends string> {
  value: T
  label: string
  icon?: ReactNode
  hint?: string
}

/** A sheet of mutually exclusive choices: status, series, format. */
export function PickerSheet<T extends string>({
  open,
  title,
  options,
  value,
  onPick,
  onClose,
}: {
  open: boolean
  title: string
  options: Option<T>[]
  value: T | null
  onPick: (value: T) => void
  onClose: () => void
}) {
  return (
    <Sheet open={open} onClose={onClose} label={title}>
      <div className="picker">
        <h2 className="picker-title">{title}</h2>
        <ul className="picker-list" role="listbox" aria-label={title}>
          {options.map((option) => (
            <li key={option.value}>
              <button
                type="button"
                role="option"
                aria-selected={option.value === value}
                className="picker-option"
                onClick={() => {
                  onPick(option.value)
                  onClose()
                }}
              >
                {option.icon && <span className="picker-icon">{option.icon}</span>}
                <span className="picker-label">{option.label}</span>
                {option.hint && <span className="picker-hint mono">{option.hint}</span>}
                <span className="picker-check" aria-hidden="true">
                  {option.value === value && <CheckIcon size={18} />}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </Sheet>
  )
}
